// import {
//   QUESTION_TYPE_DEFAULT,
//   QUESTION_TYPE_ENPS,
//   QUESTION_TYPE_DROPDOWN,
//   QUESTION_TYPE_ICONS,
//   QUESTION_TYPE_MULTIPLE_CHOICE,
//   QUESTION_TYPE_MESSAGE,
//   QUESTION_TYPE_RATING,
//   QUESTION_TYPE_TEXT,
//   QUESTION_TYPE_YES_NO,
//   QUESTION_TYPES
// } from '../constants/question_types';

import DEFAULT_QUESTION_TYPE, * as QUESTION_TYPES from '../constants/question_types';

// Each icon name has a prefix at the beginning which needs to be stripped out
// in order to match the icon file name.
const ICON_DB_PREFIXES = ['sliding', 'fixed', 'yes_no'];

/**
 * Normalizes the `messagesPerLanguage` property of a `question` from the api.
 *
 * Example:
 * ```
 * {
 *   'en-ca': {
 *     message: "I am given an opportunity to improve my skills in my business unit.",
 *     description: null,
 *     comments: null,
 *     labelLow: "Strongly Disagree",
 *     labelHigh: "Strongly Agree"
 *   },
 *   'fr': {
 *     message: "On me donne l'occasion d'améliorer mes compétences dans mon unité d'affaires.",
 *     description: null,
 *     comments: null,
 *     labelLow: "Fortement en désaccord",
 *     labelHigh: "Entièrement d'accord"
 *   }
 * }
 * ```
 */
const createLanguagesDict = (messages) =>
  messages.reduce(
    (dict, msg) => ({
      ...dict,
      [msg.language.langISOCode]: {
        title: msg.message,
        description: msg.description || null,
        labelLow: msg.labelBefore || null,
        labelHigh: msg.labelAfter || null,
      },
    }),
    {},
  );

// Strips question type text from database icon labels (if they exist) or
// returns null if there is no icon value.
const createIconName = (icon) => {
  if (!icon) return null;

  // Strip prefix characters from name string.
  const iconName = ICON_DB_PREFIXES.reduce((name, prefix) => {
    return name.replace(`${prefix}_`, '');
  }, icon.name);

  return iconName;
};

/**
 * Example choices:
 * ```
 * [
 *   {
 *     id: 111,
 *     position: 1,
 *     languages: {
 *       'en-ca': 'This is the label for options 1',
 *       'fr': 'Ceci est l\'étiquette des options 1'
 *     }
 *   },
 *   {
 *     id: 222,
 *     position: 2,
 *     languages: {
 *       'en-ca': 'This is the label for options 2',
 *       'fr': 'Ceci est l\'étiquette des options 2'
 *     }
 *   }
 * ]
 * ```
 */
const createMultipleChoices = (choices) => {
  return choices.map((choice) => ({
    id: choice.id,
    position: choice.orderIndex,
    languages: choice.messagesPerLanguage.reduce(
      (dict, msg) => ({
        ...dict,
        [msg.language.langISOCode]: msg.message,
      }),
      {},
    ),
  }));
};

const createQuestionType = (type) => {
  return QUESTION_TYPES.LIST.includes(type) ? type : DEFAULT_QUESTION_TYPE;
};

/**
 * NOTE: For server data format, see `findSurvey` query docs in `puse2.0-admin`
 * file `/docs/graphql/surveys.md`.
 * For `languages`, all types include `messagesPerLanguage` attr except for
 * multiple-choice & dropdown. For those, it will simply be an empty object.
 * Instead, they will include an `options` property which contains a list of
 * language labels per list.
 *
 * Example:
 * ```
 * {
 *   id: 123,
 *   position: 5,
 *   type: 'SLIDING',
 *   icon: 'teal_square',
 *   isRequired: true,
 *   isCommentable: false,
 *   hasLabels: true,
 *   hasDescription: true,
 *   hasSkipButton: false,
 *   ratingLow: 0,
 *   ratingHigh: 10,
 *   languages: {...} // See `createLanguagesDict()` above.
 * }
 * ```
 *
 * or
 *
 * ```
 * {
 *   id: 123,
 *   position: 5,
 *   type: 'MULTIPLE',
 *   icon: 'teal_square',
 *   isRequired: true,
 *   isCommentable: false,
 *   hasLabels: true,
 *   hasDescription: true,
 *   hasSkipButton: false,
 *   languages: {...},
 *   minRequiredSelections: 0,
 *   maxRequiredSelections: 2,
 *   options: [...] // See `createMultipleChoices()` above.
 * }
 * ```
 */
const toNormalizedQuestion = (q, logics) => {
  const questionType = createQuestionType(q.type.name);
  const displayConditions = logics.filter((logic) => logic.affectedSurveyElementId === q.id);
  const controlConditions = logics.filter((logic) => logic.mainSurveyElementId === q.id);

  const commonProps = {
    id: q.id,
    position: q.orderIndex,
    type: questionType,
    icon: createIconName(q.question.icon),
    isRequired: q.question.required,
    isCommentable: q.question.showTextComment,
    hasDescription: q.question.showDescription,
    hasLabels: q.question.showLabels,
    hasSkipButton: q.showSkipButton,
    languages: createLanguagesDict(q.question.messagesPerLanguage),
    displayConditions,
    controlConditions,
  };

  switch (questionType) {
    case QUESTION_TYPES.MESSAGE:
      return {
        ...commonProps,
        hasSkipButton: true,
      };
    case QUESTION_TYPES.ENPS:
    case QUESTION_TYPES.RATING:
      return {
        ...commonProps,
        ratingLow: q.question.ratingOptionLowest,
        ratingHigh: q.question.ratingOptionHighest,
      };
    case QUESTION_TYPES.DROPDOWN:
      return {
        ...commonProps,
        options: createMultipleChoices(q.question.choices),
      };
    case QUESTION_TYPES.MULTIPLE_CHOICE: {
      return {
        ...commonProps,
        minRequiredSelections: q.question.minSelectedOption,
        maxRequiredSelections: q.question.maxSelectedOption,
        options: createMultipleChoices(q.question.choices),
      };
    }
    case QUESTION_TYPES.YES_NO:
    case QUESTION_TYPES.ICONS:
    case QUESTION_TYPES.TEXT:
    default:
      return commonProps;
  }
};

// Sort by property `orderIndex` from lowest to highest.
const byOrderIndex = (a, b) => {
  return a.orderIndex - b.orderIndex;
};

const normalizedQuestions = (rawQuestionData, logics) => {
  return rawQuestionData.sort(byOrderIndex).map((question) => toNormalizedQuestion(question, logics));
};

export { normalizedQuestions, toNormalizedQuestion, byOrderIndex };
